import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { Button } from '../../components/ui/button';
import { 
  Users, Mail, Download, RefreshCw, LogOut, Calendar,
  Copy, CheckCircle, AlertCircle, Search
} from 'lucide-react';

const AdminSubscribersPage = () => {
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();

  // Check if admin is logged in
  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/login');
    }
  }, [navigate]);

  // Fetch subscribers
  const fetchSubscribers = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/subscribers`);
      const data = await response.json();
      setSubscribers(data.subscribers || []);
    } catch (err) {
      setError('Failed to fetch subscribers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubscribers();
  }, []);

  // Logout
  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    navigate('/login');
  };

  // Copy all emails
  const copyAllEmails = () => {
    const emails = subscribers.map(s => s.email).join('\n');
    navigator.clipboard.writeText(emails);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Download CSV
  const downloadCSV = () => {
    const csv = ['email,subscribed_at,source'];
    subscribers.forEach(s => {
      csv.push(`${s.email},${s.subscribed_at},${s.source || 'unknown'}`);
    });
    const blob = new Blob([csv.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `pricestrike-subscribers-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  // Filter subscribers
  const filteredSubscribers = subscribers.filter(s => 
    s.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Format date
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-[#0A0F1C]">
      <Header />
      
      <section className="pt-40 pb-20 px-6">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="text-3xl font-bold text-[#F8FAFC] mb-2">
                Email Subscribers
              </h1>
              <p className="text-[#94A3B8]">
                Launch waitlist signups from Coming Soon & Login pages
              </p>
            </div>
            <div className="flex gap-3">
              <Link to="/admin/analytics">
                <Button variant="outline" className="border-[#2563EB]/50 text-[#F8FAFC] hover:bg-[#2563EB]/10">
                  View Analytics
                </Button>
              </Link>
              <Button 
                onClick={handleLogout}
                variant="outline" 
                className="border-red-500/50 text-red-400 hover:bg-red-500/10"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <div className="bg-gradient-to-br from-[#111827] to-[#0A0F1C] border border-[#2563EB]/20 rounded-xl p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#2563EB]/10 rounded-lg flex items-center justify-center">
                  <Users className="w-6 h-6 text-[#2563EB]" />
                </div>
                <div>
                  <p className="text-3xl font-bold text-[#F8FAFC]">{subscribers.length}</p>
                  <p className="text-sm text-[#94A3B8]">Total Subscribers</p>
                </div>
              </div>
            </div>
            <div className="bg-gradient-to-br from-[#111827] to-[#0A0F1C] border border-[#2563EB]/20 rounded-xl p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-green-500/10 rounded-lg flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-green-400" />
                </div>
                <div>
                  <p className="text-3xl font-bold text-[#F8FAFC]">
                    {subscribers.filter(s => {
                      const date = new Date(s.subscribed_at);
                      const today = new Date();
                      return date.toDateString() === today.toDateString();
                    }).length}
                  </p>
                  <p className="text-sm text-[#94A3B8]">Today</p>
                </div>
              </div>
            </div>
            <div className="bg-gradient-to-br from-[#111827] to-[#0A0F1C] border border-[#2563EB]/20 rounded-xl p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-purple-500/10 rounded-lg flex items-center justify-center">
                  <Mail className="w-6 h-6 text-purple-400" />
                </div>
                <div>
                  <p className="text-3xl font-bold text-[#F8FAFC]">
                    {subscribers.filter(s => {
                      const date = new Date(s.subscribed_at);
                      const weekAgo = new Date();
                      weekAgo.setDate(weekAgo.getDate() - 7);
                      return date > weekAgo;
                    }).length}
                  </p>
                  <p className="text-sm text-[#94A3B8]">This Week</p>
                </div>
              </div>
            </div>
          </div>

          {/* Actions Bar */}
          <div className="bg-gradient-to-br from-[#111827] to-[#0A0F1C] border border-[#2563EB]/20 rounded-xl p-4 mb-6">
            <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
              {/* Search */}
              <div className="relative w-full md:w-80">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#94A3B8]" />
                <input
                  type="text"
                  placeholder="Search emails..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full bg-[#0A0F1C] border border-[#2563EB]/30 rounded-lg pl-10 pr-4 py-2 text-white placeholder-[#94A3B8] focus:outline-none focus:border-[#2563EB]"
                />
              </div>
              
              {/* Action Buttons */}
              <div className="flex gap-3">
                <Button
                  onClick={fetchSubscribers}
                  variant="outline"
                  className="border-[#2563EB]/50 text-[#F8FAFC] hover:bg-[#2563EB]/10"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Refresh
                </Button>
                <Button
                  onClick={copyAllEmails}
                  variant="outline"
                  className="border-[#2563EB]/50 text-[#F8FAFC] hover:bg-[#2563EB]/10"
                >
                  {copied ? (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2 text-green-400" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4 mr-2" />
                      Copy All
                    </>
                  )}
                </Button>
                <Button
                  onClick={downloadCSV}
                  className="bg-[#2563EB] hover:bg-[#1d4ed8] text-white"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Export CSV
                </Button>
              </div>
            </div>
          </div>

          {/* Error State */}
          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-6 flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-400" />
              <p className="text-red-400">{error}</p>
            </div>
          )}

          {/* Subscribers Table */}
          <div className="bg-gradient-to-br from-[#111827] to-[#0A0F1C] border border-[#2563EB]/20 rounded-xl overflow-hidden">
            {loading ? (
              <div className="p-12 text-center">
                <div className="w-8 h-8 border-2 border-[#2563EB]/30 border-t-[#2563EB] rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-[#94A3B8]">Loading subscribers...</p>
              </div>
            ) : filteredSubscribers.length === 0 ? (
              <div className="p-12 text-center">
                <Mail className="w-12 h-12 text-[#94A3B8] mx-auto mb-4" />
                <p className="text-[#F8FAFC] font-semibold mb-2">No subscribers yet</p>
                <p className="text-[#94A3B8] text-sm">
                  {searchTerm ? 'No emails match your search' : 'Subscribers will appear here when people sign up'}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[#2563EB]/20">
                      <th className="text-left text-sm font-medium text-[#94A3B8] p-4">#</th>
                      <th className="text-left text-sm font-medium text-[#94A3B8] p-4">Email</th>
                      <th className="text-left text-sm font-medium text-[#94A3B8] p-4">Source</th>
                      <th className="text-left text-sm font-medium text-[#94A3B8] p-4">Subscribed</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredSubscribers.map((subscriber, index) => (
                      <tr 
                        key={subscriber.id} 
                        className="border-b border-[#2563EB]/10 hover:bg-[#2563EB]/5 transition-colors"
                      >
                        <td className="p-4 text-[#94A3B8]">{index + 1}</td>
                        <td className="p-4">
                          <a 
                            href={`mailto:${subscriber.email}`}
                            className="text-[#F8FAFC] hover:text-[#2563EB] transition-colors"
                          >
                            {subscriber.email}
                          </a>
                        </td>
                        <td className="p-4">
                          <span className="text-xs bg-[#2563EB]/10 text-[#2563EB] px-2 py-1 rounded">
                            {subscriber.source || 'unknown'}
                          </span>
                        </td>
                        <td className="p-4 text-[#94A3B8] text-sm">
                          {formatDate(subscriber.subscribed_at)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Footer Note */}
          <p className="text-center text-[#94A3B8] text-sm mt-6">
            Emails are collected from{' '}
            <Link to="/coming-soon" className="text-[#2563EB] hover:underline">/coming-soon</Link>
            {' '}page
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AdminSubscribersPage;
